import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader } from "../components/ui/card";
import { Lock, ArrowRight, Eye, EyeOff, Sparkles } from "lucide-react";
import { easeApple, staggerContainer, itemReveal } from "../lib/motion";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_sales-pipeline-hub-24/artifacts/mmegsjtv_Ameenji.logo%20Black%20.png";

const BG_URL =
  "https://images.unsplash.com/photo-1671722294182-ed01cbe66bd1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA0MTJ8MHwxfHNlYXJjaHw0fHxtb2Rlcm4lMjBpbmR1c3RyaWFsJTIwZmFjdG9yeSUyMG9mZmljZSUyMHRlYW0lMjBtaW5pbWFsaXN0fGVufDB8fHx8MTc3Mzc4Njg2M3ww&ixlib=rb-4.1.0&q=85";

const HIGHLIGHTS = [
  { value: "100%", label: "Lead tracking" },
  { value: "Real-time", label: "Sales pipeline" },
  { value: "Smart", label: "Auto assignment" },
];

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email.trim(), password.trim());
      navigate("/dashboard");
    } catch (err) {
      setError(err.response?.data?.detail || "Login failed. Check your credentials.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-slate-50">
      {/* Left — Cinematic visual panel */}
      <div
        className="hidden lg:flex lg:flex-1 relative overflow-hidden"
        style={{
          backgroundImage: `url(${BG_URL})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        {/* layered gradient + grain */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#0E3A3E]/95 via-[#114346]/90 to-[#155155]/85" />
        <div
          className="absolute inset-0 opacity-[0.18] mix-blend-overlay pointer-events-none"
          style={{
            backgroundImage:
              "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/></filter><rect width='100%' height='100%' filter='url(%23n)' opacity='0.55'/></svg>\")",
          }}
        />
        {/* floating gold glow */}
        <motion.div
          aria-hidden
          className="absolute -top-20 -right-16 h-80 w-80 rounded-full bg-[#FBBA00]/25 blur-3xl"
          animate={{ scale: [1, 1.08, 1], opacity: [0.35, 0.55, 0.35] }}
          transition={{ duration: 8, ease: "easeInOut", repeat: Infinity }}
        />
        <motion.div
          aria-hidden
          className="absolute bottom-10 -left-10 h-72 w-72 rounded-full bg-[#2b8a8f]/30 blur-3xl"
          animate={{ scale: [1, 1.12, 1], opacity: [0.3, 0.5, 0.3] }}
          transition={{ duration: 10, ease: "easeInOut", repeat: Infinity, delay: 1.2 }}
        />

        <motion.div
          variants={staggerContainer}
          initial="initial"
          animate="animate"
          className="relative z-10 flex flex-col justify-end p-14 w-full"
        >
          <motion.span
            variants={itemReveal}
            className="inline-flex items-center gap-2 self-start mb-6 px-3 py-1 rounded-full text-[11px] tracking-widest uppercase font-semibold text-[#FBBA00] bg-white/5 border border-white/10 backdrop-blur-md"
          >
            <Sparkles size={12} className="text-[#FBBA00]" />
            Ameenji CRM
          </motion.span>
          <motion.h1
            variants={itemReveal}
            className="text-5xl xl:text-6xl font-heading font-bold text-white leading-[1.04] tracking-tight"
          >
            Excellence.<br />
            <span className="text-[#FBBA00]">Endurance.</span>
          </motion.h1>
          <motion.p
            variants={itemReveal}
            className="text-slate-200/85 text-lg font-body max-w-md mt-5 leading-relaxed"
          >
            Every lead, every order, every conversation — in one quiet, premium workspace.
          </motion.p>

          <motion.div
            variants={itemReveal}
            className="mt-10 grid grid-cols-3 gap-6 max-w-lg"
          >
            {HIGHLIGHTS.map((h, i) => (
              <div key={h.label} className="relative">
                {i > 0 && (
                  <span className="absolute -left-3 top-1 bottom-1 w-px bg-white/10" />
                )}
                <p className="text-2xl xl:text-3xl font-heading font-bold text-white">
                  {h.value}
                </p>
                <p className="text-[11px] uppercase tracking-wider text-slate-400 mt-1">
                  {h.label}
                </p>
              </div>
            ))}
          </motion.div>
        </motion.div>
      </div>

      {/* Right — Sign-in card */}
      <div className="flex-1 flex items-center justify-center p-6">
        <motion.div
          initial={{ opacity: 0, y: 14, filter: "blur(4px)" }}
          animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
          transition={{ duration: 0.55, ease: easeApple }}
          className="w-full max-w-md"
        >
          <div className="flex justify-center mb-8">
            <motion.img
              src={LOGO_URL}
              alt="Ameenji"
              className="h-14 select-none"
              initial={{ opacity: 0, scale: 0.92 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, ease: easeApple, delay: 0.1 }}
              draggable={false}
            />
          </div>

          <Card className="card-premium border-slate-200/60 shadow-premium-lg">
            <CardHeader className="text-center pb-2 pt-7">
              <h2 className="text-[26px] font-heading font-bold text-slate-900 tracking-tight">
                Welcome back
              </h2>
              <p className="text-sm text-slate-500 font-body mt-1.5">
                Sign in to continue to your workspace
              </p>
            </CardHeader>
            <CardContent className="pt-5 pb-7">
              <motion.form
                onSubmit={handleSubmit}
                className="space-y-5"
                variants={staggerContainer}
                initial="initial"
                animate="animate"
              >
                <motion.div variants={itemReveal} className="space-y-2">
                  <Label htmlFor="email" className="text-[13px] font-medium text-slate-700">
                    Login ID
                  </Label>
                  <Input
                    id="email"
                    type="text"
                    placeholder="Enter your login ID"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="h-11 bg-slate-50/60 border-slate-200 rounded-lg focus:bg-white focus:ring-2 focus:ring-[#FBBA00]/35 focus:border-[#FBBA00] transition-all duration-200 ease-[cubic-bezier(0.16,1,0.3,1)]"
                    data-testid="login-email-input"
                    required
                  />
                </motion.div>

                <motion.div variants={itemReveal} className="space-y-2">
                  <Label htmlFor="password" className="text-[13px] font-medium text-slate-700">
                    Password
                  </Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      placeholder="Enter your password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="h-11 bg-slate-50/60 border-slate-200 rounded-lg focus:bg-white focus:ring-2 focus:ring-[#FBBA00]/35 focus:border-[#FBBA00] pr-10 transition-all duration-200 ease-[cubic-bezier(0.16,1,0.3,1)]"
                      data-testid="login-password-input"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700 transition-colors duration-200"
                      data-testid="login-password-toggle"
                      tabIndex={-1}
                    >
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </motion.div>

                {error && (
                  <motion.div
                    initial={{ opacity: 0, y: -4 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, ease: easeApple }}
                    className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2"
                    data-testid="login-error-message"
                  >
                    {error}
                  </motion.div>
                )}

                <motion.p variants={itemReveal} className="text-xs text-slate-400 text-center">
                  <Link
                    to="/forgot-password"
                    className="text-[#155155] hover:text-[#0E4040] font-medium hover:underline transition-colors"
                    data-testid="login-forgot-password-link"
                  >
                    Forgot your password?
                  </Link>
                </motion.p>

                <motion.div variants={itemReveal}>
                  <Button
                    type="submit"
                    className="btn-magnetic w-full h-11 bg-[#155155] hover:bg-[#0E4040] text-white font-semibold shadow-sm rounded-lg"
                    disabled={loading}
                    data-testid="login-submit-btn"
                  >
                    {loading ? (
                      <span className="flex items-center gap-2">
                        <span className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        Signing in…
                      </span>
                    ) : (
                      <span className="flex items-center gap-2">
                        <Lock size={15} />
                        Sign In
                        <ArrowRight size={15} className="transition-transform group-hover:translate-x-0.5" />
                      </span>
                    )}
                  </Button>
                </motion.div>
              </motion.form>
            </CardContent>
          </Card>

          <p className="text-center text-xs text-slate-400 mt-6 font-body tracking-wide">
            Ameenji Rubber Limited &middot; Internal CRM System
          </p>
        </motion.div>
      </div>
    </div>
  );
}
